import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InfiniteScrollContainer } from "components/common/InfiniteScrollContainer";
import { ConfirmActionModal } from "../../ConfirmActionModal";
import { GridReviewTile } from "../GridReviewTile";
import { GridReviewToolbar } from "../GridReviewToolbar";
import { useChunkedList } from "../useChunkedList";
import { useGridBulkActions } from "../useGridBulkActions";
import { useGridSelection } from "../useGridSelection";
import { usePageImageLoader } from "../usePageImageLoader";
import type { SelectOption } from "components/common/Select/types";
import type { GridReviewModalContentProps } from "./types";

const TILE_CHUNK_SIZE = 250;

export const GridReviewModalContent = ({
  plan,
  elements,
  isAllElementsMode,
  currentGroupLabel,
  isSelectable,
  onBulkDeleteElements,
  onBulkMoveElements,
  onJumpToElement,
  onScopeEmptied,
}: GridReviewModalContentProps) => {
  const { t } = useTranslation();

  const { loadPageImage } = usePageImageLoader();

  const { selectedElementIds, toggleElementSelection, clearSelection } =
    useGridSelection({ elements });

  const handleActionSucceeded = useCallback(
    (affectedElementIds: string[]) => {
      clearSelection();
      // Moving within "all elements" never empties the scope; the ids stay in the plan.
      const isScopeEmptied =
        !isAllElementsMode && affectedElementIds.length >= elements.length;
      if (isScopeEmptied) {
        onScopeEmptied();
      }
    },
    [clearSelection, isAllElementsMode, elements.length, onScopeEmptied],
  );

  const {
    isBulkActionRunning,
    isDeleteConfirmOpen,
    openDeleteConfirm,
    closeDeleteConfirm,
    deleteSelectedElements,
    moveTargetGroupLabel,
    setMoveTargetGroupLabel,
    isMoveConfirmOpen,
    openMoveConfirm,
    closeMoveConfirm,
    moveSelectedElements,
  } = useGridBulkActions({
    floorPlanId: plan.floorPlanId,
    selectedElementIds,
    onBulkDeleteElements,
    onBulkMoveElements,
    onActionSucceeded: handleActionSucceeded,
  });

  const { visibleItems, hasMoreItems, showMoreItems } = useChunkedList({
    items: elements,
    chunkSize: TILE_CHUNK_SIZE,
  });

  const groupOptions = useMemo<SelectOption[]>(
    () =>
      plan.groupedElements.groupIds.map((groupLabel) => ({
        value: groupLabel,
        label: groupLabel,
      })),
    [plan.groupedElements.groupIds],
  );

  if (elements.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center text-sm text-gray-500">
        {t("smartSurveyor.gridReview.emptyState")}
      </div>
    );
  }

  return (
    <>
      {isSelectable && (
        <GridReviewToolbar
          selectedCount={selectedElementIds.size}
          groupOptions={groupOptions}
          currentGroupLabel={currentGroupLabel}
          moveTargetGroupLabel={moveTargetGroupLabel}
          isBusy={isBulkActionRunning}
          onMoveTargetGroupLabelChange={setMoveTargetGroupLabel}
          onClearSelection={clearSelection}
          onMoveSelected={openMoveConfirm}
          onDeleteSelected={openDeleteConfirm}
        />
      )}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
        <InfiniteScrollContainer
          isActive={hasMoreItems}
          rootMargin="600px"
          onEndReached={showMoreItems}
        >
          <div className="grid grid-cols-[repeat(auto-fill,minmax(8rem,1fr))] gap-2">
            {visibleItems.map((element, elementIndex) => {
              const pageIndex = element.page ?? 0;
              const pageDimensions = plan.pageDimensionsByIndex.get(pageIndex);
              return (
                <GridReviewTile
                  key={element.id}
                  element={element}
                  orderNumber={elementIndex + 1}
                  imageUrl={element.floorPlanImage ?? plan.planImage}
                  pageScaleFactor={plan.pageScaleFactors.get(pageIndex) ?? 1}
                  sourceWidthPx={pageDimensions?.widthPx ?? plan.imageWidthPx}
                  sourceHeightPx={
                    pageDimensions?.heightPx ?? plan.imageHeightPx
                  }
                  isSelected={selectedElementIds.has(element.id)}
                  isSelectable={isSelectable}
                  showGroupCaption={isAllElementsMode}
                  loadPageImage={loadPageImage}
                  onToggle={toggleElementSelection}
                  onJump={onJumpToElement}
                />
              );
            })}
          </div>
        </InfiniteScrollContainer>
      </div>
      <ConfirmActionModal
        isOpen={isDeleteConfirmOpen}
        onClose={closeDeleteConfirm}
        onConfirm={deleteSelectedElements}
        action="delete"
        title={t("smartSurveyor.gridReview.deleteConfirmTitle")}
        description={t("smartSurveyor.gridReview.deleteConfirmDescription", {
          count: selectedElementIds.size,
        })}
      />
      <ConfirmActionModal
        isOpen={isMoveConfirmOpen}
        onClose={closeMoveConfirm}
        onConfirm={moveSelectedElements}
        action="move"
        title={t("smartSurveyor.gridReview.moveConfirmTitle")}
        description={t("smartSurveyor.gridReview.moveConfirmDescription", {
          count: selectedElementIds.size,
          group: moveTargetGroupLabel,
        })}
      />
    </>
  );
};
